import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/lib/api'
import type { Course, CourseModule, CourseReview, Lesson, PageMeta, Readiness } from '@/lib/types'

export interface CourseFilters {
  q?: string
  /** ID pengajar; membatasi pada kelas miliknya. */
  expert?: string
  category?: string
  level?: string
  format?: string
  sort?: string
  max_price?: number
  page?: number
}

interface CourseList {
  courses: Course[]
  meta: PageMeta
}

export function useCourses(filters: CourseFilters) {
  return useQuery({
    queryKey: ['courses', filters],
    queryFn: () => api.get<CourseList>('/courses', { query: { ...filters, per_page: 12 } }),
    // Katalog jarang berubah dalam hitungan detik; cache yang sedikit lebih
    // lama membuat perpindahan halaman terasa seketika.
    staleTime: 60_000,
  })
}

export function useCourse(slug: string) {
  return useQuery({
    queryKey: ['course', slug],
    queryFn: () => api.get<{ course: Course }>(`/courses/${slug}`),
    select: (data) => data.course,
  })
}

export function useCourseReviews(slug: string) {
  return useQuery({
    queryKey: ['course-reviews', slug],
    queryFn: () => api.get<{ reviews: CourseReview[]; meta: PageMeta }>(`/courses/${slug}/reviews`),
  })
}

export function useCategories() {
  return useQuery({
    queryKey: ['course-categories'],
    queryFn: () => api.get<{ categories: Array<{ slug: string; count: number }> }>('/courses/categories'),
    staleTime: 5 * 60_000,
  })
}

// --- Pengelolaan kelas oleh Tenaga Ahli ------------------------------------

export function useMyCourses(status?: string) {
  return useQuery({
    queryKey: ['my-courses', status],
    queryFn: () => api.get<CourseList>('/me/courses', { query: { status, per_page: 50 } }),
  })
}

export interface ManagedCourse {
  course: Course
  readiness: Readiness
  landing_page: unknown[]
}

export function useManagedCourse(courseId: string) {
  return useQuery({
    queryKey: ['managed-course', courseId],
    queryFn: () => api.get<ManagedCourse>(`/manage/courses/${courseId}`),
  })
}

export interface CreateCourseInput {
  title: string
  subtitle?: string
  description?: string
  category: string
  level?: string
  format?: string
  price: number
}

export function useCreateCourse() {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (input: CreateCourseInput) => api.post<{ course: Course }>('/manage/courses', input),
    onSuccess: () => client.invalidateQueries({ queryKey: ['my-courses'] }),
  })
}

export interface UpdateCourseInput {
  title?: string
  subtitle?: string
  description?: string
  category?: string
  level?: string
  format?: string
  thumbnail_url?: string
  price?: number
  discount_price?: number
  clear_discount?: boolean
  capacity?: number
  clear_capacity?: boolean
  duration_minutes?: number
}

export function useUpdateCourse(courseId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (input: UpdateCourseInput) => api.patch<{ course: Course }>(`/manage/courses/${courseId}`, input),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['managed-course', courseId] })
      client.invalidateQueries({ queryKey: ['my-courses'] })
    },
  })
}

export function usePublishCourse(courseId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: () => api.post<{ course: Course }>(`/manage/courses/${courseId}/publish`),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['managed-course', courseId] })
      client.invalidateQueries({ queryKey: ['my-courses'] })
    },
  })
}

export function useArchiveCourse(courseId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: () => api.post<{ course: Course }>(`/manage/courses/${courseId}/archive`),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['managed-course', courseId] })
      client.invalidateQueries({ queryKey: ['my-courses'] })
    },
  })
}

export function useAddModule(courseId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (input: { title: string; summary?: string }) =>
      api.post<{ module: CourseModule }>(`/manage/courses/${courseId}/modules`, {
        title: input.title,
        summary: input.summary ?? '',
      }),
    onSuccess: () => client.invalidateQueries({ queryKey: ['managed-course', courseId] }),
  })
}

export function useUpdateModule(courseId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (input: { moduleId: string; title: string; summary: string }) =>
      api.patch<void>(`/manage/modules/${input.moduleId}`, { title: input.title, summary: input.summary }),
    onSuccess: () => client.invalidateQueries({ queryKey: ['managed-course', courseId] }),
  })
}

export function useDeleteModule(courseId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (moduleId: string) => api.delete<void>(`/manage/modules/${moduleId}`),
    onSuccess: () => client.invalidateQueries({ queryKey: ['managed-course', courseId] }),
  })
}

export interface LessonInput {
  title: string
  content_type?: string
  content?: string
  asset_url?: string
  duration_minutes: number
  is_preview: boolean
  drip_days: number
}

export function useAddLesson(courseId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (input: LessonInput & { moduleId: string }) => {
      const { moduleId, ...body } = input
      return api.post<{ lesson: Lesson }>(`/manage/modules/${moduleId}/lessons`, body)
    },
    onSuccess: () => client.invalidateQueries({ queryKey: ['managed-course', courseId] }),
  })
}

export function useUpdateLesson(courseId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (input: Partial<LessonInput> & { lessonId: string }) => {
      const { lessonId, ...body } = input
      return api.patch<void>(`/manage/lessons/${lessonId}`, body)
    },
    onSuccess: () => client.invalidateQueries({ queryKey: ['managed-course', courseId] }),
  })
}

export function useDeleteLesson(courseId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (lessonId: string) => api.delete<void>(`/manage/lessons/${lessonId}`),
    onSuccess: () => client.invalidateQueries({ queryKey: ['managed-course', courseId] }),
  })
}
